import React, { Dispatch, SetStateAction, createContext, useMemo } from 'react';
import { useTransactions } from '../hooks/useTransactions'; // adjust path as needed
import { ReasonRecord } from '../types/ReasonRecord';
import { StoreRecord } from '../types/StoreRecord';
import { TransactionRecord } from '../types/TransactionRecord';
import { TypesRecord } from '../types/TypesRecord';
import { SelectChangeEvent } from '@mui/material';

export interface TransactionsContextValues {
  isMobile: boolean;

  // States
  transactions: TransactionRecord[];
  transactionTypes: TypesRecord[];
  transactionReasons: ReasonRecord[];
  stores: StoreRecord[];
  isLoading: boolean;
  startDate: Date;
  endDate: Date;
  searchTerm: string;
  type: string;
  reason: string;
  sourceID: string;
  source: string;
  fieldToSort: string;
  sortDirection: 'asc' | 'desc';
  dropdownStore: string;
  openModal: boolean;
  page: number;
  totalCount: number;
  hasPermissionToCreateNew: boolean;
  isLoadingTypes: boolean;
  sources: string[];
  operationalStart: Date | null;
  operationalEnd: Date | null;
  isShiftEnabled: boolean;

  // Setters
  setTransactions: Dispatch<SetStateAction<TransactionRecord[]>>;
  setTransactionTypes: Dispatch<SetStateAction<TypesRecord[]>>;
  setTransactionReasons: Dispatch<SetStateAction<ReasonRecord[]>>;
  setStores: Dispatch<SetStateAction<StoreRecord[]>>;
  setStartDate: Dispatch<SetStateAction<Date>>;
  setEndDate: Dispatch<SetStateAction<Date>>;
  setSearchTerm: Dispatch<SetStateAction<string>>;
  setType: Dispatch<SetStateAction<string>>;
  setReason: Dispatch<SetStateAction<string>>;
  setSourceID: Dispatch<SetStateAction<string>>;
  setSource: Dispatch<SetStateAction<string>>;
  setFieldToSort: Dispatch<SetStateAction<string>>;
  setSortDirection: Dispatch<SetStateAction<'asc' | 'desc'>>;
  setDropdownStore: Dispatch<SetStateAction<string>>;
  setOpenModal: Dispatch<SetStateAction<boolean>>;
  setPage: Dispatch<SetStateAction<number>>;
  setTotalCount: Dispatch<SetStateAction<number>>;

  // Functions
  loadTransactions: (storeNum?: string, newpage?: number) => Promise<void>;
  loadTransactionTypes: (storeNum?: string) => Promise<TypesRecord[]>;
  loadTransactionReasons: (storeNum?: string) => Promise<void>;
  changePage: (newPage: number) => void;
  reloadTransactions: (storeNum?: string) => void;
  reloadDropDowns: (storeNum?: string) => void;
  handleCancelModal: () => void;
  handleConfirmModal: () => Promise<void>;
  getDateOnly: (date: Date) => string;
  handleSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleStoreChange: (event: SelectChangeEvent) => void;
  handleTypeChange: (event: SelectChangeEvent) => void;
  handleReasonChange: (event: SelectChangeEvent) => void;
  handleSourceIDChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleSourceChange: (event: SelectChangeEvent) => void;
  handleStartDateChange: (date: string | null) => void;
  handleEndDateChange: (date: string | null) => void;
  handleClearButtonClick: () => void;
  handleSearchButtonClick: () => void;
}

export const TransactionsContext = createContext<null | TransactionsContextValues>(null);

interface ProviderProps {
  children: React.ReactNode;
}

export const TransactionsProvider = ({ children }: ProviderProps) => {
  const values = useTransactions();

  const passedValue: TransactionsContextValues = {
    ...values,
  };

  const contextValue = useMemo(() => passedValue, [passedValue]);

  return (
    <TransactionsContext.Provider value={contextValue}>
      {children}
    </TransactionsContext.Provider>
  );
};
